import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';

const PrivacyPolicy = () => {
  return (
    <div className="min-vh-100" style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' }}>
      <div className="container py-5">
        {/* Header */}
        <div className="text-white mb-4">
          <Link to="/" style={{ textDecoration: 'none' }}>
            <Logo color="white" width="300px" />
          </Link>
        </div>

        {/* Content Card */}
        <div className="card shadow-lg" style={{ borderRadius: '12px' }}>
          <div className="card-body p-5">
            <h2 className="mb-4" style={{ fontWeight: '700', color: '#2c3e50' }}>Privacy Policy</h2>
            <p className="text-muted mb-4" style={{ fontSize: '0.875rem' }}>
              Last Updated: November 4, 2025
            </p>

            <div style={{ lineHeight: '1.8', color: '#4b5563' }}>
              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>1. Introduction</h4>
                <p>
                  Votewave Services ( "we", "us", or "our") operates VoteWave, an online voting platform. 
                  This Privacy Policy explains how we collect, use, disclose, and safeguard your personal information when you use our services.
                </p>
                <p>
                  We are committed to protecting your privacy and ensuring the security of your personal data. By using VoteWave, 
                  you consent to the data practices described in this policy.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>2. Information We Collect</h4>
                
                <p><strong>2.1 Personal Information:</strong></p>
                <ul>
                  <li>Full name</li>
                  <li>Email address</li>
                  <li>Password (encrypted)</li>
                  <li>Organization affiliation</li>
                  <li>Profile information (for candidates)</li>
                </ul>
                <p><strong>Note:</strong> We do <strong>NOT</strong> collect or store payment card information, credit card numbers, CVVs, or mobile money PINs. All payment processing is handled directly by Flutterwave, our secure third-party payment provider.</p>

                <p><strong>2.2 Voting Data:</strong></p>
                <ul>
                  <li>Vote records (anonymized for confidentiality)</li>
                  <li>Voting timestamps</li>
                  <li>Election participation history</li>
                  <li>IP addresses for fraud prevention</li>
                </ul>

                <p><strong>2.3 Technical Information:</strong></p>
                <ul>
                  <li>Browser type and version</li>
                  <li>Device information</li>
                  <li>Operating system</li>
                  <li>IP address</li>
                  <li>Cookies and usage data</li>
                </ul>

                <p><strong>2.4 Transaction Data:</strong></p>
                <ul>
                  <li>Payment transaction IDs (reference numbers)</li>
                  <li>Transaction amounts and currency</li>
                  <li>Transaction status and timestamps</li>
                  <li>Payment method type (e.g., card, mobile money) - but NOT card numbers or credentials</li>
                </ul>
                <p><strong>Important:</strong> VoteWave only stores transaction metadata (amounts, references, status). We never store actual payment credentials. All sensitive payment information remains exclusively with Flutterwave.</p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>3. How We Use Your Information</h4>
                <p>We use the collected information for the following purposes:</p>
                <ul>
                  <li><strong>Account Management:</strong> Creating and managing user accounts</li>
                  <li><strong>Service Delivery:</strong> Facilitating elections, vote casting, and result tabulation</li>
                  <li><strong>Payment Processing:</strong> Processing transactions for multiple voting features</li>
                  <li><strong>Communication:</strong> Sending election notifications, updates, and important announcements</li>
                  <li><strong>Security:</strong> Detecting and preventing fraud, unauthorized access, and vote manipulation</li>
                  <li><strong>Analytics:</strong> Improving our services and understanding user behavior</li>
                  <li><strong>Compliance:</strong> Meeting legal and regulatory obligations</li>
                  <li><strong>Customer Support:</strong> Responding to inquiries and providing assistance</li>
                </ul>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>4. Vote Confidentiality</h4>
                <p>
                  We take vote confidentiality seriously. While we record that a user voted and store vote records in our database, 
                  individual voting choices are kept confidential. Results are presented in aggregate form only.
                </p>
                <p>
                  Organization administrators and super admins cannot see individual voting choices. They can only access 
                  aggregate results and statistics showing total vote counts per candidate.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>5. Data Sharing and Disclosure</h4>
                <p><strong>5.1 Within Organizations:</strong> Voting results and participant data may be shared with organization administrators as configured by the organization.</p>
                
                <p><strong>5.2 Service Providers:</strong> We may share data with trusted third-party service providers who assist us in:</p>
                <ul>
                  <li>Payment processing</li>
                  <li>Email delivery</li>
                  <li>Cloud hosting and storage</li>
                  <li>Analytics and performance monitoring</li>
                </ul>

                <p><strong>5.3 Legal Requirements:</strong> We may disclose information if required by law, court order, or government request, or to protect our rights and safety.</p>

                <p><strong>5.4 Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, user data may be transferred to the acquiring entity.</p>

                <p><strong>We do not sell your personal information to third parties for marketing purposes.</strong></p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>6. Data Security</h4>
                <p>We implement industry-standard security measures to protect your data:</p>
                <ul>
                  <li><strong>Encryption:</strong> Data transmission is encrypted using SSL/TLS protocols</li>
                  <li><strong>Secure Storage:</strong> Passwords are hashed using bcrypt encryption</li>
                  <li><strong>Access Controls:</strong> Role-based access restrictions limit data exposure</li>
                  <li><strong>Regular Audits:</strong> Security audits and vulnerability assessments</li>
                  <li><strong>Backup Systems:</strong> Regular data backups for disaster recovery</li>
                  <li><strong>Payment Security:</strong> Payment processing handled by Flutterwave, which maintains PCI-DSS compliance for all payment data</li>
                </ul>
                <p>
                  However, no method of transmission over the internet is 100% secure. While we strive to protect your data, 
                  we cannot guarantee absolute security.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>7. Data Retention</h4>
                <p>We retain your personal information for as long as:</p>
                <ul>
                  <li>Your account remains active</li>
                  <li>Required to provide services to you</li>
                  <li>Necessary for legal, accounting, or reporting requirements</li>
                  <li>Required to resolve disputes or enforce our agreements</li>
                </ul>
                <p>
                  Election data, including votes and results, are retained indefinitely for audit and historical purposes 
                  unless deletion is requested by the organization or required by law.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>8. Your Rights</h4>
                <p>You have the following rights regarding your personal data:</p>
                <ul>
                  <li><strong>Access:</strong> Request a copy of the personal information we hold about you</li>
                  <li><strong>Correction:</strong> Update or correct inaccurate information</li>
                  <li><strong>Deletion:</strong> Request deletion of your personal data (subject to legal obligations)</li>
                  <li><strong>Objection:</strong> Object to certain processing of your data</li>
                  <li><strong>Data Portability:</strong> Request your data in a machine-readable format</li>
                  <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where applicable</li>
                </ul>
                <p>To exercise these rights, please contact us at info@votewave.co.ug</p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>9. Cookies and Tracking</h4>
                <p>
                  We use cookies and similar tracking technologies to enhance your experience on VoteWave. Cookies help us:
                </p>
                <ul>
                  <li>Maintain your login session</li>
                  <li>Remember your preferences</li>
                  <li>Analyze site usage and performance</li>
                  <li>Detect and prevent fraud</li>
                </ul>
                <p>
                  You can control cookie settings through your browser preferences. However, disabling cookies may affect 
                  certain features of the platform.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>10. Email Communications</h4>
                <p>We may send you:</p>
                <ul>
                  <li><strong>Transactional Emails:</strong> Account verification, password resets, vote confirmations</li>
                  <li><strong>Service Updates:</strong> Election notifications, platform updates, security alerts</li>
                  <li><strong>Marketing Emails:</strong> Product updates and features (you can opt-out anytime)</li>
                </ul>
                <p>
                  Platform emails are delivered through our email service provider. Emails may be sent from "@rallyhq.co.ug" or "@votewave.co.ug" domains.
                </p>
                <p>
                  You can unsubscribe from marketing emails by clicking the unsubscribe link in any email. 
                  Transactional and service emails are necessary for platform operation and cannot be disabled.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>11. Children's Privacy</h4>
                <p>
                  VoteWave is not intended for use by individuals under the age of 16. We do not knowingly collect 
                  personal information from children. If we become aware that we have collected data from a child, 
                  we will take steps to delete such information promptly.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>12. Third-Party Links</h4>
                <p>
                  VoteWave may contain links to third-party websites or services. We are not responsible for the privacy 
                  practices of these external sites. We encourage you to review their privacy policies before providing 
                  any personal information.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>13. International Data Transfers</h4>
                <p>
                  Your information may be transferred to and processed in countries other than Uganda. We ensure that 
                  such transfers comply with applicable data protection laws and that your data receives adequate protection.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>14. Changes to This Privacy Policy</h4>
                <p>
                  We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. 
                  We will notify you of significant changes via email or platform notification. The "Last Updated" date at the 
                  top of this policy indicates when it was last revised.
                </p>
                <p>
                  Your continued use of VoteWave after changes are made constitutes acceptance of the updated policy.
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>15. Contact Us</h4>
                <p><strong>VoteWave Services</strong></p>
                <p>
                  If you have questions, concerns, or requests regarding this Privacy Policy or our data practices, 
                  please contact us at:
                </p>
                <p>
                  Email: info@votewave.co.ug<br />
                  Website: www.votewave.co.ug<br />
                </p>
              </section>

              <section className="mb-4">
                <h4 style={{ fontWeight: '600', color: '#2c3e50', marginBottom: '1rem' }}>16. Consent</h4>
                <p>
                  By using VoteWave, you acknowledge that you have read and understood this Privacy Policy and consent 
                  to the collection, use, and disclosure of your personal information as described herein.
                </p>
              </section>
            </div>

            <hr className="my-4" />

            <div className="d-flex justify-content-between align-items-center">
              <Link to="/" className="btn btn-outline-primary">
                <i className="bi bi-arrow-left me-2"></i>
                Back to Home
              </Link>
              <Link to="/terms-and-conditions" className="btn btn-link">
                Terms and Conditions
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
